import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { UserIcon } from '@heroicons/react/24/outline';
import LoadingSkeleton from './LoadingSkeleton';

const UserSearchDropdown = ({
  users = [],
  isLoading = false,
  error = null,
  showNoResults = false,
  onUserSelect,
  onClose,
  onRetry,
  position = { top: 0, left: 0 },
  isVisible = false,
  maxHeight = 300,
  className = '',
  searchStartTime = null,
  showLoadingState = false,
  loadingMessage = "Searching...",
  skeletonCount = 3,
  usePortal = false,
  containerRef = null
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSlowSearchMessage, setShowSlowSearchMessage] = useState(false);
  const [absolutePosition, setAbsolutePosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const userRefs = useRef([]);
  const slowSearchTimeoutRef = useRef(null);
  
  // Reset selection when users change
  useEffect(() => {
    setSelectedIndex(-1);
    userRefs.current = [];
  }, [users]);

  // Calculate absolute position when using portal
  useEffect(() => {
    if (usePortal && containerRef?.current && isVisible) {
      const updateAbsolutePosition = () => {
        const containerRect = containerRef.current.getBoundingClientRect();
        setAbsolutePosition({
          top: containerRect.top + position.top,
          left: containerRect.left + position.left
        });
      };

      updateAbsolutePosition();
      
      // Update position on scroll or resize
      const handleUpdate = () => updateAbsolutePosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [usePortal, containerRef, position, isVisible]);

  // Handle slow search timeout
  useEffect(() => {
    if (isLoading && searchStartTime) {
      // Clear any existing timeout
      if (slowSearchTimeoutRef.current) {
        clearTimeout(slowSearchTimeoutRef.current);
      }
      
      // Set timeout for 2 seconds to show "Still searching..." message
      slowSearchTimeoutRef.current = setTimeout(() => {
        setShowSlowSearchMessage(true);
      }, 2000);
    } else {
      // Clear timeout and reset message when not loading
      if (slowSearchTimeoutRef.current) {
        clearTimeout(slowSearchTimeoutRef.current);
        slowSearchTimeoutRef.current = null;
      }
      setShowSlowSearchMessage(false);
    }

    // Cleanup on unmount
    return () => {
      if (slowSearchTimeoutRef.current) {
        clearTimeout(slowSearchTimeoutRef.current);
      }
    };
  }, [isLoading, searchStartTime]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isVisible || users.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < users.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : users.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < users.length) {
          onUserSelect?.(users[selectedIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
        
      default:
        break;
    }
  }, [isVisible, users, selectedIndex, onUserSelect, onClose]);
  
  // Attach keyboard listener
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, handleKeyDown]);
  
  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && userRefs.current[selectedIndex]) {
      userRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    
    if (isVisible) {
      // Add a small delay to prevent immediate closing when dropdown becomes visible
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, onClose]);
  
  // Handle user selection
  const handleUserClick = (user, index) => {
    setSelectedIndex(index);
    onUserSelect?.(user);
  };
  
  if (!isVisible) {
    return null;
  }
  
  // Use absolute position for portal, relative position for normal rendering
  const finalPosition = usePortal ? absolutePosition : position;
  const positionClass = usePortal ? 'fixed' : 'absolute';
  const zIndex = usePortal ? 'z-[60]' : 'z-50'; // Higher z-index when using portal to appear above modals
  
  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`${positionClass} ${zIndex} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden ${className}`}
      style={{
        top: finalPosition.top,
        left: finalPosition.left,
        height: `${maxHeight}px`,
        minWidth: window.innerWidth < 768 ? 'calc(100vw - 20px)' : '280px',
        maxWidth: window.innerWidth < 768 ? 'calc(100vw - 20px)' : '400px',
        touchAction: 'manipulation'
      }}
      role="listbox"
      aria-label="User search results"
      aria-expanded="true"
      aria-activedescendant={selectedIndex >= 0 ? `user-option-${selectedIndex}` : undefined}
    >
      {/* Loading State */}
      {(isLoading || showLoadingState) && (
        <div className="p-3">
          {/* Loading message with spinner */}
          <div className="flex items-center justify-center mb-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
            {showSlowSearchMessage ? "Still searching..." : loadingMessage}
          </div>
          
          {/* Skeleton placeholders */}
          <div className="space-y-2">
            {[...Array(skeletonCount)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <LoadingSkeleton className="w-8 h-8 rounded-full" />
                <div className="flex-1">
                  <LoadingSkeleton className="h-4 w-24 mb-1" />
                  <LoadingSkeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Error State */}
      {error && !isLoading && !showLoadingState && (
        <div className="p-4 text-center">
          <p className="text-sm text-red-600 dark:text-red-400 mb-2">
            {error}
          </p>
          {!error.includes('cached results') && (
            <button
              onClick={() => {
                if (onRetry) {
                  onRetry();
                } else if (onClose) {
                  onClose();
                }
              }}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline"
            >
              Try again
            </button>
          )}
        </div>
      )}
      
      {/* No Results */}
      {showNoResults && !isLoading && !error && (
        <div className="p-4 text-center">
          <UserIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No users found
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Try a different search term
          </p>
        </div>
      )}
      
      {/* User Results */}
      {users.length > 0 && !isLoading && !showLoadingState && (
        <div 
          className="scrollbar-styled transition-opacity duration-200 ease-in-out" 
          style={{ 
            maxHeight: `${Math.max(150, maxHeight - 80)}px`,
            overscrollBehavior: 'contain'
          }}
        >
          {users.map((user, index) => (
            <div
              key={user.id}
              ref={el => userRefs.current[index] = el}
              className={`flex items-center p-3 cursor-pointer transition-colors touch-manipulation ${
                selectedIndex === index
                  ? 'bg-purple-50 dark:bg-purple-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600'
              }`}
              style={{ 
                minHeight: '44px', // Ensure minimum touch target size for accessibility
                minWidth: '44px'   // Ensure minimum width as well
              }}
              onClick={() => handleUserClick(user, index)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="option"
              aria-selected={selectedIndex === index}
              aria-label={`Select ${user.displayName}${user.currentRole ? `, ${user.currentRole}` : ''}`}
              id={`user-option-${index}`}
              tabIndex={-1}
            >
              {/* Profile Picture */}
              <div className="flex-shrink-0 mr-3">
                {user.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt={`${user.displayName}'s profile`}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center ${
                    user.profilePicture ? 'hidden' : 'flex'
                  }`}
                >
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {user.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              </div>
              
              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.displayName}
                  </p>
                </div>
                
                {(user.currentRole || user.companyName) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {[user.currentRole, user.companyName].filter(Boolean).join(' at ')}
                  </p>
                )}
              </div>
              
              {/* Selection Indicator */}
              {selectedIndex === index && (
                <div className="flex-shrink-0 ml-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Footer with keyboard hints */}
      {users.length > 0 && !isLoading && (
        <div className="border-t border-gray-200 dark:border-gray-600 px-3 py-2 bg-gray-50 dark:bg-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use ↑↓ to navigate, Enter to select, Esc to close
          </p>
        </div>
      )}
    </div>
  );

  // Use portal if requested (typically when inside a modal)
  if (usePortal) {
    return createPortal(dropdownContent, document.body);
  }

  return dropdownContent;
};

export default UserSearchDropdown;